import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import {
  Search, Trash2, FileText, X, ChevronLeft, ChevronRight, Filter, ExternalLink, RotateCcw,
  Loader2, Columns3, ArrowUp, ArrowDown, BookX, Rocket, Send, Edit3, Workflow, Check, Cpu,
} from 'lucide-react';


const STATUS_STYLES = {
  draft:           'bg-slate-100 text-slate-600 border border-slate-300',
  review:          'bg-amber-50 text-amber-700 border border-amber-200',
  published:       'bg-emerald-50 text-emerald-700 border border-emerald-200',
  unpublished:     'bg-slate-100 text-slate-500 border border-slate-300',
  refused:         'bg-red-50 text-red-600 border border-red-200',
  failed:          'bg-red-100 text-red-700 border border-red-300',
  needs_attention: 'bg-orange-50 text-orange-700 border border-orange-200',
  approved:        'bg-emerald-50 text-emerald-700 border border-emerald-200',
};

const STATUS_LABELS = {
  draft: 'Draft', review: 'In Review', published: 'Published',
  unpublished: 'Unpublished', refused: 'Refused',
  failed: 'Failed', needs_attention: 'Needs Attention', approved: 'Published',
};

const SORT_OPTIONS = [
  { value: 'updated',  label: 'Last updated' },
  { value: 'public',   label: 'Public date' },
  { value: 'created',  label: 'Newest first' },
  { value: 'oldest',   label: 'Oldest first' },
  { value: 'headline', label: 'Headline A–Z' },
];

const ASC_SORTS = ['oldest', 'headline'];

const SUBTABS = ['all', 'draft', 'review', 'published', 'unpublished', 'attention', 'deleted'];

const SUBTAB_LABELS = {
  all: 'All', draft: 'Drafts', review: 'In Review', published: 'Published',
  unpublished: 'Unpublished', refused: 'Refused', attention: 'Attention', deleted: 'Deleted',
};

const SUBTAB_STATUS = {
  all: null, draft: 'draft', review: 'review', published: 'published',
  unpublished: 'unpublished', attention: 'failed,needs_attention', deleted: null,
};

const DEFAULT_SORT = {
  review: 'oldest', draft: 'oldest',
  published: 'public', unpublished: 'public',
  attention: 'updated', all: 'updated', deleted: 'updated',
};

const COLUMNS = [
  { id: 'status',      label: 'Status' },
  { id: 'publication', label: 'Publication' },
  { id: 'source',      label: 'Source' },
  { id: 'byline',      label: 'Byline' },
  { id: 'model',       label: 'Model' },
  { id: 'tags',        label: 'Tags' },
  { id: 'public_date', label: 'Public date', sorts: ['public'] },
  { id: 'created',     label: 'Created',     sorts: ['created', 'oldest'] },
  { id: 'updated',     label: 'Updated',     sorts: ['updated'] },
];

const VERB = {
  publish: 'published', unpublish: 'unpublished', review: 'sent to review',
  delete: 'moved to Deleted', restore: 'restored', permanent: 'permanently deleted',
};

const emptyFilters = {
  publication_id:      'all',
  source_id:           'all',
  tag:                 'all',
  geography:           '',
  date_from:           '',
  date_to:             '',
  date_published_from: '',
  date_published_to:   '',
};

const FILTER_DEFAULT = (key) => emptyFilters[key];

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

const LS_COLS    = 'okebourne.newsroom.hiddenCols';
const LS_DENSITY = 'okebourne.newsroom.density';

const readStored = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeStored = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
};

const fmtUK = (value, withTime = true) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};

const getLiveUrl = (story) => {
  if (!story.publication_domain || !story.slug) return null;
  const tag = story.tags?.[0]
    ? story.tags[0].toLowerCase().replace(/\s+/g, '-')
    : 'news';
  return `https://${story.publication_domain}/${tag}/${story.slug}`;
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full text-xs px-2 py-0.5 font-medium whitespace-nowrap ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function ColumnsMenu({ hidden, onToggle, onReset, density, onDensity }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(o => !o)}
        className={`border-border text-foreground hover:bg-slate-50 gap-1.5 h-10 ${open ? 'bg-slate-100 border-slate-300' : ''}`}
      >
        <Columns3 size={14} />
        Columns
        {hidden.size > 0 && <span className="text-[11px] text-slate-400">{COLUMNS.length - hidden.size}/{COLUMNS.length}</span>}
      </Button>

      {open && (
        <div className="absolute right-0 mt-1 z-50 w-56 rounded-lg border border-border bg-white shadow-lg p-1.5 animate-in fade-in-0 zoom-in-95 duration-150">
          <p className="px-2 py-1 text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Density</p>
          <div className="flex gap-1 p-1">
            {[['compact', 'Compact'], ['comfortable', 'Comfortable']].map(([id, lbl]) => (
              <button
                key={id}
                onClick={() => onDensity(id)}
                className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${density === id ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                {lbl}
              </button>
            ))}
          </div>

          <p className="px-2 pt-2 pb-1 text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Show columns</p>
          <div className="max-h-64 overflow-y-auto">
            {COLUMNS.map(c => {
              const on = !hidden.has(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => onToggle(c.id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-foreground hover:bg-slate-50 transition-colors"
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                    {on && <Check size={11} className="text-white" />}
                  </span>
                  {c.label}
                </button>
              );
            })}
          </div>

          {hidden.size > 0 && (
            <button onClick={onReset} className="w-full text-left px-2 py-1.5 mt-1 rounded-md text-xs text-slate-600 hover:bg-slate-50 border-t border-border">
              Show all columns
            </button>
          )}
        </div>
      )}
    </div>
  );
}


export default function AllStoriesTab() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initStatus = searchParams.get('status') || 'all';
  const initSort   = searchParams.get('sort')   || DEFAULT_SORT[initStatus] || 'updated';

  const [status, setStatus]           = useState(initStatus);
  const [sort, setSort]               = useState(initSort);
  const [search, setSearch]           = useState(searchParams.get('search') || '');
  const [filters, setFilters]         = useState(emptyFilters);
  const [showFilters, setShowFilters] = useState(false);

  const [stories, setStories]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [counts, setCounts]         = useState(null);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const [selected, setSelected]     = useState(() => new Set());
  const [busyId, setBusyId]         = useState(null);
  const [bulkBusy, setBulkBusy]     = useState(false);
  const [confirm, setConfirm]       = useState(null);

  const [hiddenCols, setHiddenCols] = useState(() => new Set(readStored(LS_COLS, [])));
  const [density, setDensity]       = useState(() => readStored(LS_DENSITY, 'comfortable'));

  const [publications, setPublications] = useState([]);
  const [sources, setSources]           = useState([]);
  const [allTags, setAllTags]           = useState([]);
  const [refLoaded, setRefLoaded]       = useState(false);

  const statusRef   = useRef(status);
  const sortRef     = useRef(sort);
  const filtersRef  = useRef(filters);
  const searchRef   = useRef(search);
  const pageRef     = useRef(page);
  const searchInput = useRef(null);
  const debounceRef = useRef(null);
  const lastIdxRef  = useRef(null);

  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { pageRef.current    = page;    }, [page]);
  useEffect(() => { writeStored(LS_COLS, [...hiddenCols]); }, [hiddenCols]);
  useEffect(() => { writeStored(LS_DENSITY, density); }, [density]);

  const showCol = (id) => !hiddenCols.has(id);

  const countFor = (sub) => {
    if (!counts) return null;
    return counts[sub] ?? 0;
  };

  useEffect(() => {
    const loadRef = async () => {
      try {
        const ov = await api.get('/feeds/overview');
        setPublications((ov.data.publications || []).map(p => ({ id: p.id, name: p.name })));
      } catch { /* non-critical */ }
      try {
        const sr = await api.get('/feeds/sources');
        const list = Array.isArray(sr.data) ? sr.data : (sr.data.sources || []);
        setSources(list.map(s => ({ id: s.id, name: s.source_name })));
      } catch { /* non-critical */ }
      try {
        const tg = await api.get('/tags');
        setAllTags((tg.data || []).map(t => t.name || t));
      } catch { /* tags endpoint optional */ }
      setRefLoaded(true);
    };
    loadRef();
  }, []);

  const syncUrl = useCallback(() => {
    const sp = {};
    if (statusRef.current && statusRef.current !== 'all') sp.status = statusRef.current;
    if (sortRef.current) sp.sort = sortRef.current;
    if (searchRef.current) sp.search = searchRef.current;
    setSearchParams(sp, { replace: true });
  }, [setSearchParams]);

  const fetchStories = useCallback(async (pageNum = 1) => {
    setLoading(true);
    try {
      const f = filtersRef.current;
      const params = {
        limit: PAGE_SIZE,
        offset: (pageNum - 1) * PAGE_SIZE,
        sort: sortRef.current,
      };
      const st = statusRef.current;
      if (st === 'deleted') {
        params.deleted = true;
      } else if (SUBTAB_STATUS[st]) {
        params.status = SUBTAB_STATUS[st];
      }
      if (searchRef.current)             params.search = searchRef.current;
      if (f.publication_id !== 'all')    params.publication_id = f.publication_id;
      if (f.source_id !== 'all')         params.source_id = f.source_id;
      if (f.tag !== 'all')               params.tag = f.tag;
      if (f.date_from)                   params.date_from = f.date_from;
      if (f.date_to)                     params.date_to = f.date_to;
      if (f.date_published_from)         params.date_published_from = f.date_published_from;
      if (f.date_published_to)           params.date_published_to = f.date_published_to;

      const res = await api.get('/feeds/stories', { params });
      const t = res.data.total || 0;
      setStories(res.data.stories || []);
      setTotal(t);
      setTotalPages(Math.max(1, Math.ceil(t / PAGE_SIZE)));
      setPage(pageNum);
      setSelected(new Set());
      lastIdxRef.current = null;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load stories');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const f = filtersRef.current;
      const params = {};
      if (f.publication_id !== 'all') params.publication_id = f.publication_id;
      if (f.source_id !== 'all')      params.source_id = f.source_id;
      const res = await api.get('/feeds/stories/counts', { params });
      setCounts(res.data);
    } catch {
      /* badges are non-critical */
    }
  }, []);

  useEffect(() => {
    if (refLoaded) {
      searchRef.current = search;
      fetchStories(1);
      fetchCounts();
    }
  }, [refLoaded]);

  const refresh = async () => {
    await fetchStories(pageRef.current);
    fetchCounts();
  };

  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (e.key === '/' && !typing) { e.preventDefault(); searchInput.current?.focus(); }
      if (e.key === 'Escape' && !typing) setSelected(new Set());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const changeStatus = (newStatus) => {
    statusRef.current = newStatus;
    setStatus(newStatus);
    sortRef.current = DEFAULT_SORT[newStatus] || 'updated';
    setSort(sortRef.current);
    setSelected(new Set());
    syncUrl();
    fetchStories(1);
  };

  const changeSort = (v) => {
    sortRef.current = v;
    setSort(v);
    syncUrl();
    fetchStories(1);
  };

  const clickSort = (sorts) => {
    if (!sorts?.length) return;
    const idx = sorts.indexOf(sortRef.current);
    changeSort(sorts[(idx + 1) % sorts.length]);
  };

  const runSearch = (value) => {
    searchRef.current = value;
    syncUrl();
    fetchStories(1);
  };

  const onSearchChange = (value) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { if (refLoaded) runSearch(value); }, SEARCH_DEBOUNCE_MS);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    clearTimeout(debounceRef.current);
    runSearch(search);
  };

  const clearSearch = () => {
    clearTimeout(debounceRef.current);
    setSearch('');
    runSearch('');
    searchInput.current?.focus();
  };

  const setFilter = (key, val) => {
    const next = { ...filtersRef.current, [key]: val };
    filtersRef.current = next;
    setFilters(next);
  };

  const applyFilters = () => { fetchStories(1); fetchCounts(); };

  const removeFilter = (key) => {
    setFilter(key, FILTER_DEFAULT(key));
    fetchStories(1);
    fetchCounts();
  };

  const activeFilterCount = Object.entries(filters).filter(([k, v]) => v !== FILTER_DEFAULT(k)).length;

  const clearFilters = () => {
    filtersRef.current = emptyFilters;
    setFilters(emptyFilters);
    fetchStories(1);
    fetchCounts();
  };

  const chips = useMemo(() => {
    const f = filters;
    const out = [];
    const push = (key, label) => out.push({ key, label });
    if (f.publication_id !== 'all') push('publication_id', `Publication: ${publications.find(p => p.id === f.publication_id)?.name || f.publication_id}`);
    if (f.source_id !== 'all')      push('source_id', `Source: ${sources.find(s => s.id === f.source_id)?.name || f.source_id}`);
    if (f.tag !== 'all')            push('tag', `Tag: ${f.tag}`);
    if (f.geography)                push('geography', `Geography: ${f.geography}`);
    if (f.date_from)                push('date_from', `Created from ${f.date_from}`);
    if (f.date_to)                  push('date_to', `Created to ${f.date_to}`);
    if (f.date_published_from)      push('date_published_from', `Published from ${f.date_published_from}`);
    if (f.date_published_to)        push('date_published_to', `Published to ${f.date_published_to}`);
    return out;
  }, [filters, publications, sources]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchStories(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const doAction = async (id, action) => {
    setBusyId(id);
    try {
      if (action === 'delete')         await api.delete(`/feeds/stories/${id}`);
      else if (action === 'permanent') await api.delete(`/feeds/stories/${id}/permanent`);
      else                             await api.post(`/feeds/stories/${id}/${action}`, {});
      toast.success(`Story ${VERB[action] || action}`);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const askDelete = (story) => setConfirm({
    title: 'Delete story',
    body: `Move "${story.headline || 'Untitled'}" to the Deleted tab? It's kept for 7 days, then permanently removed.`,
    confirmLabel: 'Delete',
    onConfirm: () => doAction(story.id, 'delete'),
  });

  const askPermanent = (story) => setConfirm({
    title: 'Delete permanently',
    body: `Permanently delete "${story.headline || 'Untitled'}"? This cannot be undone and the source article will not be regenerated.`,
    confirmLabel: 'Delete permanently',
    onConfirm: () => doAction(story.id, 'permanent'),
  });

  const askUnpublish = (story) => setConfirm({
    title: 'Unpublish story',
    body: `Take "${story.headline || 'Untitled'}" off the live site? It can be republished later.`,
    confirmLabel: 'Unpublish',
    onConfirm: () => doAction(story.id, 'unpublish'),
  });

  const runBulk = async (action) => {
    const ids  = [...selected];
    const byId = new Map(stories.map(s => [s.id, s]));
    setBulkBusy(true);
    const results = await Promise.allSettled(ids.map(id =>
      action === 'delete'    ? api.delete(`/feeds/stories/${id}`) :
      action === 'permanent' ? api.delete(`/feeds/stories/${id}/permanent`) :
                               api.post(`/feeds/stories/${id}/${action}`, {})
    ));
    const failures = [];
    results.forEach((r, i) => {
      if (r.status === 'rejected') {
        failures.push({ headline: byId.get(ids[i])?.headline || 'Untitled' });
      }
    });
    const ok = ids.length - failures.length;
    setSelected(new Set());
    setBulkBusy(false);
    setConfirm(null);
    await refresh();
    (failures.length ? toast.error : toast.success)(
      `${ok} ${VERB[action] || action}${failures.length ? `, ${failures.length} failed` : ''}`
    );
  };

  const askBulk = (action, label) => {
    if (!['delete', 'unpublish', 'permanent'].includes(action)) return runBulk(action);
    const n = `${selected.size} ${selected.size === 1 ? 'story' : 'stories'}`;
    setConfirm({
      title: `${label} ${n}`,
      body: action === 'permanent'
        ? 'This permanently deletes the selected stories and cannot be undone.'
        : action === 'delete'
          ? 'The selected stories move to the Deleted tab, kept for 7 days then permanently removed.'
          : 'This takes the selected stories off the live site.',
      confirmLabel: label,
      onConfirm: () => runBulk(action),
    });
  };

  const toggleSelect = (id, idx, shift) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (shift && lastIdxRef.current !== null && lastIdxRef.current !== idx) {
        const lo = Math.min(lastIdxRef.current, idx);
        const hi = Math.max(lastIdxRef.current, idx);
        const turnOn = !next.has(id);
        for (let i = lo; i <= hi; i++) {
          const sid = stories[i]?.id;
          if (!sid) continue;
          if (turnOn) next.add(sid); else next.delete(sid);
        }
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    lastIdxRef.current = idx;
  };

  const allOnPageSelected = stories.length > 0 && stories.every(s => selected.has(s.id));

  const toggleSelectAll = () => setSelected(prev => {
    const next = new Set(prev);
    if (stories.every(s => next.has(s.id))) stories.forEach(s => next.delete(s.id));
    else stories.forEach(s => next.add(s.id));
    lastIdxRef.current = null;
    return next;
  });

  const uniqueTags = [...new Set(allTags)].sort();

  const compact = density === 'compact';
  const cellY   = compact ? 'py-1.5' : 'py-3';
  const thBase  = `px-3 ${compact ? 'py-2' : 'py-3'} text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap`;
  const tdBase  = `px-3 ${cellY} align-middle whitespace-nowrap`;
  const iconBtn = 'h-7 w-7 inline-flex items-center justify-center rounded-md transition-colors disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200';

  const openStory = (story, mode = 'view') => {
    if (!story.feed_item_id) { toast.error('No source item linked to this story'); return; }
    const params = new URLSearchParams({ tab: 'generated' });
    if (mode === 'edit') params.set('edit', story.id);
    navigate(`/feeds/items/${story.feed_item_id}?${params.toString()}`);
  };

  const renderActions = (story) => {
    const busy    = busyId === story.id;
    const liveUrl = getLiveUrl(story);
    const st      = story.status;

    if (busy) {
      return (
        <div className="flex items-center justify-end pr-1">
          <Loader2 size={15} className="animate-spin text-slate-400" />
        </div>
      );
    }

    if (story.deleted_at) {
      return (
        <div className="flex items-center gap-0.5 justify-end" onClick={e => e.stopPropagation()}>
          <button title="Restore" onClick={e => { e.stopPropagation(); doAction(story.id, 'restore'); }} className={`${iconBtn} text-emerald-600 hover:bg-emerald-50`}>
            <RotateCcw size={15} />
          </button>
          <button title="Delete permanently" onClick={e => { e.stopPropagation(); askPermanent(story); }} className={`${iconBtn} text-red-500 hover:bg-red-50`}>
            <Trash2 size={15} />
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-0.5 justify-end" onClick={e => e.stopPropagation()}>
        <button title="View / edit" onClick={e => { e.stopPropagation(); openStory(story, 'edit'); }} className={`${iconBtn} text-slate-600 hover:bg-slate-100`}>
          <Edit3 size={15} />
        </button>
        {st === 'review' && (
          <button title="Publish" onClick={e => { e.stopPropagation(); doAction(story.id, 'publish'); }} className={`${iconBtn} text-emerald-600 hover:bg-emerald-50`}>
            <Rocket size={15} />
          </button>
        )}
        {st === 'published' && (
          <>
            {liveUrl && (
              <button title="Visit live" onClick={e => { e.stopPropagation(); window.open(liveUrl, '_blank', 'noopener,noreferrer'); }} className={`${iconBtn} text-emerald-700 hover:bg-emerald-50`}>
                <ExternalLink size={15} />
              </button>
            )}
            <button title="Send to review" onClick={e => { e.stopPropagation(); doAction(story.id, 'review'); }} className={`${iconBtn} text-blue-600 hover:bg-blue-50`}>
              <Send size={15} />
            </button>
            <button title="Unpublish" onClick={e => { e.stopPropagation(); askUnpublish(story); }} className={`${iconBtn} text-orange-600 hover:bg-orange-50`}>
              <BookX size={15} />
            </button>
          </>
        )}
        {(st === 'unpublished' || st === 'draft') && (
          <button title="Publish" onClick={e => { e.stopPropagation(); doAction(story.id, 'publish'); }} className={`${iconBtn} text-emerald-600 hover:bg-emerald-50`}>
            <Rocket size={15} />
          </button>
        )}
        <button title="Delete" onClick={e => { e.stopPropagation(); askDelete(story); }} className={`${iconBtn} text-red-500 hover:bg-red-50`}>
          <Trash2 size={15} />
        </button>
      </div>
    );
  };

  const bulkFor = () => {
    switch (status) {
      case 'review':      return [{ action: 'publish', label: 'Publish', icon: Rocket }, { action: 'delete', label: 'Delete', icon: Trash2, danger: true }];
      case 'draft':       return [{ action: 'publish', label: 'Publish', icon: Rocket }, { action: 'delete', label: 'Delete', icon: Trash2, danger: true }];
      case 'published':   return [{ action: 'unpublish', label: 'Unpublish', icon: BookX, danger: true }, { action: 'delete', label: 'Delete', icon: Trash2, danger: true }];
      case 'unpublished': return [{ action: 'publish', label: 'Republish', icon: Rocket }, { action: 'delete', label: 'Delete', icon: Trash2, danger: true }];
      case 'deleted':     return [{ action: 'restore', label: 'Restore', icon: RotateCcw }, { action: 'permanent', label: 'Delete permanently', icon: Trash2, danger: true }];
      default:            return [{ action: 'delete', label: 'Delete', icon: Trash2, danger: true }];
    }
  };

  const emptyCopy = () => {
    if (searchRef.current) return { title: `Nothing matches "${searchRef.current}"`, hint: 'Try a shorter search, or clear the filters.' };
    if (activeFilterCount) return { title: 'No stories match these filters', hint: 'Loosen or clear a filter to widen the results.' };
    switch (status) {
      case 'review':      return { title: 'Nothing waiting for review', hint: 'Generated stories set to review appear here.' };
      case 'draft':       return { title: 'No drafts', hint: 'Draft generated stories appear here.' };
      case 'published':   return { title: 'Nothing published yet', hint: 'Published stories land here and go live on their sites.' };
      case 'unpublished': return { title: 'Nothing unpublished', hint: 'Stories taken off their live site show up here.' };
      case 'attention':   return { title: 'Nothing needs attention', hint: 'Failed or incomplete generations appear here.' };
      case 'deleted':     return { title: 'Deleted is empty', hint: 'Deleted stories stay here for 7 days before being removed.' };
      default:            return { title: 'No stories here', hint: 'Generate stories from a feed item to populate the newsroom.' };
    }
  };

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd   = Math.min(page * PAGE_SIZE, total);
  const firstLoad  = loading && stories.length === 0;

  const sortCaret = (sorts) => {
    if (!sorts?.includes(sort)) return null;
    const Icon = ASC_SORTS.includes(sort) ? ArrowUp : ArrowDown;
    return <Icon size={11} className="text-blue-600" />;
  };

  const ariaSort = (sorts) => {
    if (!sorts?.includes(sort)) return 'none';
    return ASC_SORTS.includes(sort) ? 'ascending' : 'descending';
  };

  const sortableTh = (col) => (
    <th key={col.id} className={`${thBase} bg-slate-50 border-b border-border`} aria-sort={col.sorts ? ariaSort(col.sorts) : undefined}>
      {col.sorts ? (
        <button onClick={() => clickSort(col.sorts)} className="inline-flex items-center gap-1 hover:text-foreground transition-colors uppercase tracking-wide">
          {col.label}{sortCaret(col.sorts)}
        </button>
      ) : col.label}
    </th>
  );

  const fieldCls = 'w-full bg-white text-foreground border border-border rounded-md h-9 px-3 text-sm focus:outline-none focus:border-blue-500';
  const selTrigger = 'bg-white text-foreground border-border h-9 text-sm';
  const selContent = 'bg-white border-border';
  const selItem = 'text-foreground focus:bg-slate-50';

  return (
    <div className="space-y-5 pb-16" data-testid="feeds-newsroom">

      <div className="flex gap-1 flex-wrap border-b border-border">
        {SUBTABS.map(sub => {
          const n = countFor(sub);
          const active = status === sub;
          return (
            <button
              key={sub}
              onClick={() => changeStatus(sub)}
              className={`flex items-center gap-1 px-2.5 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                active
                  ? 'border-blue-600 text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-slate-300'
              }`}
            >
              {SUBTAB_LABELS[sub]}
              {n != null && (
                <span className={`text-[11px] tabular-nums leading-none px-1 py-0.5 rounded ${
                  n === 0 ? 'text-slate-300' : active ? 'bg-blue-50 text-blue-700' : 'text-slate-400'
                }`}>{n}</span>
              )}
            </button>
          );
        })}
      </div>

      {status === 'deleted' && (
        <p className="text-xs text-slate-400 -mt-2">
          Deleted stories are kept for 7 days, then permanently removed. Restore anything you need before then.
        </p>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <form onSubmit={handleSearch} className="flex-1 min-w-[240px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input
            ref={searchInput}
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            placeholder="Search headline or intro…"
            className="pl-10 pr-9 h-10 rounded-lg bg-white text-foreground border-border placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-blue-200 focus-visible:border-blue-400"
          />
          {search ? (
            <button type="button" onClick={clearSearch} title="Clear search" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-foreground transition-colors">
              <X size={15} />
            </button>
          ) : (
            <kbd className="hidden sm:flex absolute right-2.5 top-1/2 -translate-y-1/2 h-5 w-5 items-center justify-center rounded border border-border bg-slate-50 text-[11px] text-slate-400 pointer-events-none">/</kbd>
          )}
        </form>

        <Select value={sort} onValueChange={changeSort}>
          <SelectTrigger className="w-40 h-10 bg-card border-border text-foreground"><SelectValue /></SelectTrigger>
          <SelectContent className={selContent}>
            {SORT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value} className={selItem}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className={`border-border text-foreground hover:bg-slate-50 gap-1.5 h-10 ${showFilters ? 'bg-slate-100 border-slate-300' : ''}`}
        >
          <Filter size={14} />
          Filters
          {activeFilterCount > 0 && (
            <span className="bg-blue-600 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">{activeFilterCount}</span>
          )}
        </Button>

        <ColumnsMenu
          hidden={hiddenCols}
          density={density}
          onDensity={setDensity}
          onToggle={id => setHiddenCols(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; })}
          onReset={() => setHiddenCols(new Set())}
        />
      </div>

      {chips.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap -mt-2">
          {chips.map(c => (
            <span key={c.key} className="inline-flex items-center gap-1 rounded-full bg-slate-50 border border-border pl-2.5 pr-1 py-1 text-xs text-slate-600">
              {c.label}
              <button onClick={() => removeFilter(c.key)} title="Remove filter" className="h-4 w-4 inline-flex items-center justify-center rounded-full hover:bg-slate-200 hover:text-foreground transition-colors">
                <X size={11} />
              </button>
            </span>
          ))}
          <button onClick={clearFilters} className="text-xs text-slate-400 hover:text-foreground ml-1 underline underline-offset-2">Clear all</button>
        </div>
      )}

      {showFilters && (
        <Card className="border-border animate-in fade-in-0 slide-in-from-top-1 duration-200">
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Publication</label>
                <Select value={filters.publication_id} onValueChange={v => setFilter('publication_id', v)}>
                  <SelectTrigger className={selTrigger}><SelectValue placeholder="All Publications" /></SelectTrigger>
                  <SelectContent className={selContent}>
                    <SelectItem value="all" className={selItem}>All Publications</SelectItem>
                    {publications.map(p => <SelectItem key={p.id} value={p.id} className={selItem}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Source</label>
                <Select value={filters.source_id} onValueChange={v => setFilter('source_id', v)}>
                  <SelectTrigger className={selTrigger}><SelectValue placeholder="All Sources" /></SelectTrigger>
                  <SelectContent className={selContent}>
                    <SelectItem value="all" className={selItem}>All Sources</SelectItem>
                    {sources.map(s => <SelectItem key={s.id} value={s.id} className={selItem}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Tag</label>
                <Select value={filters.tag} onValueChange={v => setFilter('tag', v)}>
                  <SelectTrigger className={selTrigger}><SelectValue placeholder="All Tags" /></SelectTrigger>
                  <SelectContent className={selContent}>
                    <SelectItem value="all" className={selItem}>All Tags</SelectItem>
                    {uniqueTags.map(t => <SelectItem key={t} value={t} className={selItem}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Geography</label>
                <input type="text" value={filters.geography} onChange={e => setFilter('geography', e.target.value)} placeholder="e.g. London, UK…" className={fieldCls} />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Created from</label>
                <input type="date" value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Created to</label>
                <input type="date" value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Published from</label>
                <input type="date" value={filters.date_published_from} onChange={e => setFilter('date_published_from', e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Published to</label>
                <input type="date" value={filters.date_published_to} onChange={e => setFilter('date_published_to', e.target.value)} className={fieldCls} />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              {activeFilterCount > 0 && (
                <Button variant="ghost" onClick={clearFilters} className="text-muted-foreground hover:text-foreground h-9 px-4 text-sm">Clear all</Button>
              )}
              <Button onClick={applyFilters} className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-6 text-sm">Apply filters</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between min-h-[24px]">
        <p className="text-xs text-slate-400">
          {firstLoad ? 'Loading stories…' : total === 0 ? 'No stories' : `${total} stor${total === 1 ? 'y' : 'ies'}${total > PAGE_SIZE ? ` — page ${page} of ${totalPages}` : ''}`}
        </p>
      </div>

      {firstLoad ? (
        <div className="flex items-center justify-center py-16 gap-2 text-slate-400 text-sm">
          <Loader2 size={16} className="animate-spin" /> Loading stories…
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-16">
          <FileText size={28} className="mx-auto text-slate-300 mb-3" />
          <p className="text-foreground font-medium">{emptyCopy().title}</p>
          <p className="text-sm text-slate-400 mt-1">{emptyCopy().hint}</p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-border bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className={`${thBase} bg-slate-50 border-b border-border w-10`}>
                      <button onClick={toggleSelectAll} className="text-slate-400 hover:text-blue-600 align-middle" title="Select all on page">
                        <span className={`w-4 h-4 rounded border inline-flex items-center justify-center ${allOnPageSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                          {allOnPageSelected && <Check size={11} className="text-white" />}
                        </span>
                      </button>
                    </th>
                    <th className={`${thBase} bg-slate-50 border-b border-border min-w-[240px] max-w-[420px]`}>Headline</th>
                    {showCol('status')      && <th className={`${thBase} bg-slate-50 border-b border-border`}>Status</th>}
                    {showCol('publication') && <th className={`${thBase} bg-slate-50 border-b border-border`}>Publication</th>}
                    {showCol('source')      && <th className={`${thBase} bg-slate-50 border-b border-border`}>Source</th>}
                    {showCol('byline')      && <th className={`${thBase} bg-slate-50 border-b border-border`}>Byline</th>}
                    {showCol('model')       && <th className={`${thBase} bg-slate-50 border-b border-border`}>Model</th>}
                    {showCol('tags')        && <th className={`${thBase} bg-slate-50 border-b border-border w-full`}>Tags</th>}
                    {showCol('public_date') && sortableTh(COLUMNS.find(c => c.id === 'public_date'))}
                    {showCol('created')     && sortableTh(COLUMNS.find(c => c.id === 'created'))}
                    {showCol('updated')     && sortableTh(COLUMNS.find(c => c.id === 'updated'))}
                    <th className={`${thBase} bg-slate-50 border-b border-border sticky right-0 z-10 border-l text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stories.map((story, idx) => {
                    const sel = selected.has(story.id);
                    const stickyBg = sel ? 'bg-blue-50' : 'bg-white';
                    return (
                      <tr
                        key={story.output_id}
                        onClick={(e) => toggleSelect(story.id, idx, e.shiftKey)}
                        className={`border-b border-slate-50 last:border-b-0 cursor-pointer ${sel ? 'bg-blue-50/60' : 'hover:bg-slate-50/60'}`}
                      >
                        <td className={tdBase}>
                          <span className={`w-4 h-4 rounded border inline-flex items-center justify-center ${sel ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                            {sel && <Check size={11} className="text-white" />}
                          </span>
                        </td>
                        <td className={`${tdBase} min-w-[240px] max-w-[420px]`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <Workflow size={13} className="text-violet-500 shrink-0" title="EventFlow" />
                            {story.image && (
                              <img src={story.image} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} className="w-8 h-6 object-cover rounded shrink-0 bg-slate-100" />
                            )}
                            <span className="font-medium text-foreground truncate" title={story.headline}>{story.headline}</span>
                          </div>
                        </td>
                        {showCol('status')      && <td className={tdBase}><StatusBadge status={story.status} /></td>}
                        {showCol('publication') && <td className={`${tdBase} text-slate-600 truncate max-w-[140px]`}>{story.publication_name}</td>}
                        {showCol('source')      && <td className={`${tdBase} text-slate-500 truncate max-w-[140px]`}>{story.source_name || '—'}</td>}
                        {showCol('byline')      && <td className={`${tdBase} text-slate-600 truncate max-w-[130px]`}>{story.byline_name || '—'}</td>}
                        {showCol('model')       && (
                          <td className={`${tdBase} text-slate-500`}>
                            {story.ai_model_used ? (
                              <span className="inline-flex items-center gap-1 text-[11px]"><Cpu size={11} className="text-slate-400" />{story.ai_model_used}</span>
                            ) : '—'}
                          </td>
                        )}
                        {showCol('tags') && (
                          <td className={tdBase}>
                            {story.tags?.length ? (
                              <div className="flex items-center gap-1">
                                {story.tags.slice(0, 2).map(t => (
                                  <span key={t} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px] whitespace-nowrap">{t}</span>
                                ))}
                                {story.tags.length > 2 && <span className="text-[11px] text-slate-400" title={story.tags.join(', ')}>+{story.tags.length - 2}</span>}
                              </div>
                            ) : <span className="text-slate-400">—</span>}
                          </td>
                        )}
                        {showCol('public_date') && <td className={`${tdBase} text-slate-600`}>{fmtUK(story.public_display_at || story.published_at)}</td>}
                        {showCol('created')     && <td className={`${tdBase} text-slate-600`}>{fmtUK(story.generated_at)}</td>}
                        {showCol('updated')     && <td className={`${tdBase} text-slate-600`}>{fmtUK(story.updated_at)}</td>}
                        <td className={`${tdBase} sticky right-0 z-10 border-l border-border ${stickyBg}`}>
                          {renderActions(story)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-4 pt-4 mt-2 border-t border-border flex-wrap">
              <p className="text-xs text-slate-400 order-2 sm:order-1">
                Showing <span className="font-medium text-slate-600">{rangeStart}–{rangeEnd}</span> of {total}
              </p>
              <div className="flex items-center gap-1 order-1 sm:order-2 ml-auto sm:ml-0">
                <Button variant="outline" size="sm" onClick={() => handlePageChange(page - 1)} disabled={page <= 1} className="border-border text-foreground hover:bg-slate-50 gap-1 h-8 px-3 disabled:opacity-40">
                  <ChevronLeft size={16} /> Prev
                </Button>
                <div className="flex items-center gap-1 mx-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
                    .map((p, idx) => p === '...' ? (
                      <span key={`e-${idx}`} className="px-1.5 text-slate-400 text-sm select-none">…</span>
                    ) : (
                      <button key={p} onClick={() => handlePageChange(p)} className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${p === page ? 'bg-blue-600 text-white' : 'text-foreground hover:bg-slate-50 border border-transparent hover:border-border'}`}>{p}</button>
                    ))}
                </div>
                <Button variant="outline" size="sm" onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages} className="border-border text-foreground hover:bg-slate-50 gap-1 h-8 px-3 disabled:opacity-40">
                  Next <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in fade-in-0 slide-in-from-bottom-2 duration-200">
          <div className="flex items-center gap-2 flex-wrap rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 shadow-xl">
            <span className="text-xs font-medium text-white px-1">{selected.size} selected</span>
            <span className="w-px h-5 bg-white/15" />
            {bulkFor().map(b => (
              <Button key={b.action} size="sm" variant="ghost" disabled={bulkBusy} onClick={() => askBulk(b.action, b.label)}
                className={`h-7 text-xs gap-1 hover:bg-white/10 ${b.danger ? 'text-red-300 hover:text-red-200' : 'text-white'}`}>
                <b.icon size={13} /> {b.label}
              </Button>
            ))}
            {bulkBusy && <Loader2 size={14} className="animate-spin text-white" />}
            <span className="w-px h-5 bg-white/15" />
            <button onClick={() => setSelected(new Set())} title="Clear selection (Esc)" className="h-6 w-6 inline-flex items-center justify-center rounded-md text-slate-300 hover:text-white hover:bg-white/10 transition-colors">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <Dialog open={!!confirm} onOpenChange={o => !o && setConfirm(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">{confirm?.title}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{confirm?.body}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(null)} className="text-muted-foreground hover:text-foreground">Cancel</Button>
            <Button variant="destructive" onClick={() => confirm?.onConfirm?.()} disabled={bulkBusy || !!busyId}>
              {(bulkBusy || !!busyId) && <Loader2 size={14} className="animate-spin mr-1.5" />}
              {confirm?.confirmLabel || 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}